import React, { useEffect, useState } from "react";
import {
  muestraSeccionExtras,
  muestraCategoriaExtras,
  isProductoConTamanos,
  productoRequiereSelector,
  formatearComentarioOpciones,
  parsearComentarioOpciones,
  calcularPrecioItemArmable,
} from "../mappers/productoMapper";
import { resolverImagenProducto } from "../utils/imagenProducto";

function formatearMonto(valor) {
  return Number(valor).toLocaleString("es-CL");
}

export default function ModalProducto({
  producto,
  abierto,
  onCerrar,
  onAgregar,
  extrasDisponibles = [],
  comentarioInicial = "",
  modoEdicionOpciones = false,
}) {
  const [extrasSeleccionados, setExtrasSeleccionados] = useState([]);
  const [categoriaExtrasAbierta, setCategoriaExtrasAbierta] = useState(false);
  const [comentario, setComentario] = useState("");
  const [cantidadesIngrediente, setCantidadesIngrediente] = useState(
    /** @type {Record<string, number>} */ ({})
  );
  const [tamanoSeleccionado, setTamanoSeleccionado] = useState(null);
  const [usarImagenOriginal, setUsarImagenOriginal] = useState(false);

  useEffect(() => {
    if (!abierto || !producto) {
      return;
    }

    setExtrasSeleccionados([]);
    setCategoriaExtrasAbierta(false);
    setUsarImagenOriginal(false);

    const parsed = parsearComentarioOpciones(comentarioInicial);
    const conTamanos = isProductoConTamanos(producto);

    if (conTamanos) {
      let tamano = null;
      if (parsed.tamano) {
        tamano =
          producto.tamanos.find((item) => item.nombre === parsed.tamano) ||
          producto.tamanos.find(
            (item) =>
              item.nombre.toLowerCase() === parsed.tamano.toLowerCase()
          ) ||
          null;
      }
      if (!tamano && producto.tamanos.length === 1) {
        tamano = producto.tamanos[0];
      }
      setTamanoSeleccionado(tamano);
    } else {
      setTamanoSeleccionado(null);
    }

    if (muestraSeccionExtras(producto)) {
      const cantidades = {};
      let unidades = 0;
      const maximo = Number(producto.armable_cantidad) || 0;
      for (const grupo of parsed.armado) {
        const match =
          producto.ingredientes.find((ing) => ing.nombre === grupo.nombre) ||
          producto.ingredientes.find(
            (ing) =>
              ing.nombre.toLowerCase() === String(grupo.nombre).toLowerCase()
          );
        if (!match) {
          continue;
        }
        const agregar = Math.max(0, Number(grupo.cantidad) || 0);
        const permitido = Math.max(0, maximo - unidades);
        const aplicado = Math.min(agregar, permitido);
        if (aplicado < 1) {
          continue;
        }
        cantidades[match.id] = (cantidades[match.id] || 0) + aplicado;
        unidades += aplicado;
      }
      setCantidadesIngrediente(cantidades);
    } else {
      setCantidadesIngrediente({});
    }

    if (productoRequiereSelector(producto)) {
      setComentario("");
      return;
    }

    setComentario(
      comentarioInicial &&
        !String(comentarioInicial).trim().toLowerCase().startsWith("armado:") &&
        !String(comentarioInicial).trim().toLowerCase().startsWith("tamaño:")
        ? comentarioInicial
        : ""
    );
  }, [abierto, producto, comentarioInicial]);

  if (!abierto || !producto) return null;

  const muestraExtras = muestraSeccionExtras(producto);
  const imagen = resolverImagenProducto(producto.imagen_url, "modal");
  const srcImagen = imagen
    ? usarImagenOriginal
      ? imagen.original
      : imagen.src
    : "";
  const conTamanos = isProductoConTamanos(producto);
  const requiereSelector = productoRequiereSelector(producto);
  const cantidadMaxima = muestraExtras ? Number(producto.armable_cantidad) || 0 : 0;
  const unidadesArmado = Object.values(cantidadesIngrediente).reduce(
    (acc, valor) => acc + (Number(valor) || 0),
    0
  );
  const tamanoOk = !conTamanos || Boolean(tamanoSeleccionado);
  const armadoOk = !muestraExtras || unidadesArmado <= cantidadMaxima;
  const seleccionCompleta = tamanoOk && armadoOk;

  function toggleExtra(id) {
    setExtrasSeleccionados((prev) =>
      prev.includes(id) ? prev.filter((e) => e !== id) : [...prev, id]
    );
  }

  function cambiarCantidadIngrediente(ingrediente, delta) {
    setCantidadesIngrediente((prev) => {
      const actual = Number(prev[ingrediente.id]) || 0;
      const total = Object.values(prev).reduce(
        (acc, valor) => acc + (Number(valor) || 0),
        0
      );
      if (delta > 0 && total >= cantidadMaxima) {
        return prev;
      }
      const siguiente = Math.max(0, actual + delta);
      return { ...prev, [ingrediente.id]: siguiente };
    });
  }

  const ofreceCategoriaExtras =
    muestraCategoriaExtras(producto) && extrasDisponibles.length > 0;
  const precioExtras = ofreceCategoriaExtras
    ? extrasDisponibles
        .filter((e) => extrasSeleccionados.includes(e.id))
        .reduce((acc, e) => acc + Number(e.precio), 0)
    : 0;

  const ingredientesElegidosPreview = muestraExtras
    ? producto.ingredientes
        .map((ingrediente) => ({
          nombre: ingrediente.nombre,
          cantidad: Number(cantidadesIngrediente[ingrediente.id]) || 0,
        }))
        .filter((ingrediente) => ingrediente.cantidad > 0)
    : [];
  const precioArmable = calcularPrecioItemArmable(producto, {
    tamano: tamanoSeleccionado,
    ingredientesElegidos: ingredientesElegidosPreview,
  });
  const precioMostrado =
    conTamanos && !tamanoSeleccionado
      ? Number(producto.precio)
      : requiereSelector
        ? precioArmable
        : Number(producto.precio);
  const precioTotal =
    (requiereSelector ? precioArmable : Number(producto.precio) || 0) +
    (modoEdicionOpciones ? 0 : precioExtras);
  const ingredientesConPrecio =
    muestraExtras && producto.ingredientes_con_precio === true;

  function handleAgregar() {
    if (!seleccionCompleta) {
      return;
    }

    const extrasSeleccionadosDetalle =
      modoEdicionOpciones || !ofreceCategoriaExtras
        ? []
        : extrasDisponibles.filter((extra) =>
            extrasSeleccionados.includes(extra.id)
          );

    const ingredientesElegidos = muestraExtras
      ? producto.ingredientes
          .map((ingrediente) => ({
            nombre: ingrediente.nombre,
            cantidad: Number(cantidadesIngrediente[ingrediente.id]) || 0,
          }))
          .filter((ingrediente) => ingrediente.cantidad > 0)
      : [];

    const comentarioFinal = requiereSelector
      ? formatearComentarioOpciones({
          tamanoNombre: tamanoSeleccionado?.nombre || null,
          ingredientesElegidos,
        })
      : comentario.trim() || null;

    onAgregar(producto, {
      comentario: comentarioFinal,
      extras: extrasSeleccionadosDetalle,
      tamano: tamanoSeleccionado,
      ingredientesElegidos,
    });
    onCerrar();
  }

  return (
    <div
      className="modal d-flex align-items-center justify-content-center modal-producto"
      tabIndex="-1"
      onClick={onCerrar}
    >
      <div
        className="modal-dialog modal-producto__dialog"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-content modal-producto__content rounded-4 border-0 px-3 py-2">
          <div className="modal-header border-0 pb-0">
            <h5 className="modal-title fw-bold">{producto.nombre}</h5>
            <button type="button" className="btn-close" onClick={onCerrar}></button>
          </div>

          <div className="modal-body modal-producto__body text-center pt-0">
            {imagen ? (
              <img
                src={srcImagen}
                alt={producto.nombre}
                decoding="async"
                className="rounded-4 mb-3 modal-producto__img"
                onError={() => {
                  if (!usarImagenOriginal && imagen.src !== imagen.original) {
                    setUsarImagenOriginal(true);
                  }
                }}
              />
            ) : (
              <div
                aria-hidden
                className="rounded-4 mb-3 modal-producto__img-placeholder"
              />
            )}

            {producto.descripcion && (
              <div className="mb-2 text-muted text-start modal-producto__descripcion">
                {producto.descripcion}
              </div>
            )}

            <div className="mb-3 fw-semibold modal-producto__precio">
              {conTamanos && !tamanoSeleccionado
                ? `desde $${formatearMonto(producto.precio)}`
                : `$${formatearMonto(precioMostrado)}`}
            </div>

            {conTamanos && (
              <div className="mb-3 text-start">
                <div className="fw-bold mb-1">Elige un tamaño</div>
                <div className="text-muted small mb-2">
                  {tamanoSeleccionado
                    ? tamanoSeleccionado.nombre
                    : "Selecciona un tamaño"}
                </div>
                <div className="d-flex flex-wrap gap-2">
                  {producto.tamanos.map((tamano) => {
                    const seleccionado =
                      tamanoSeleccionado?.id === tamano.id;
                    return (
                      <button
                        key={tamano.id}
                        type="button"
                        className={`ingrediente-chip${
                          seleccionado ? " is-selected" : ""
                        }`}
                        aria-pressed={seleccionado}
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => setTamanoSeleccionado(tamano)}
                      >
                        <span>{tamano.nombre}</span>
                        <span className="ingrediente-chip__precio">
                          ${formatearMonto(tamano.precio)}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {muestraExtras && (
              <div className="mb-3 text-start">
                <div className="fw-bold mb-1">
                  {ingredientesConPrecio
                    ? `Hasta ${cantidadMaxima} ${
                        cantidadMaxima === 1 ? "ingrediente" : "ingredientes"
                      }`
                    : `Hasta ${cantidadMaxima} ${
                        cantidadMaxima === 1
                          ? "ingrediente incluido"
                          : "ingredientes incluidos"
                      }`}
                </div>
                <div className="text-muted small mb-2">
                  {ingredientesConPrecio
                    ? "El precio base más lo que elijas"
                    : "Puedes repetir"}
                  {" · "}
                  {unidadesArmado} de {cantidadMaxima}
                </div>
                <div className="d-flex flex-column gap-2">
                  {producto.ingredientes.map((ingrediente) => {
                    const cantidad =
                      Number(cantidadesIngrediente[ingrediente.id]) || 0;
                    const enMaximo = unidadesArmado >= cantidadMaxima;
                    const mostrarPrecio =
                      ingredientesConPrecio && Number(ingrediente.precio) > 0;
                    return (
                      <div
                        key={ingrediente.id}
                        className="ingrediente-stepper"
                      >
                        <span className="ingrediente-stepper__nombre">
                          {ingrediente.nombre}
                          {mostrarPrecio ? (
                            <span className="ingrediente-stepper__precio">
                              {" "}
                              +${formatearMonto(ingrediente.precio)}
                            </span>
                          ) : null}
                        </span>
                        <div className="ingrediente-stepper__controles">
                          <button
                            type="button"
                            className="ingrediente-stepper__btn"
                            aria-label={`Quitar ${ingrediente.nombre}`}
                            disabled={cantidad < 1}
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={() =>
                              cambiarCantidadIngrediente(ingrediente, -1)
                            }
                          >
                            −
                          </button>
                          <span className="ingrediente-stepper__cantidad">
                            {cantidad}
                          </span>
                          <button
                            type="button"
                            className="ingrediente-stepper__btn"
                            aria-label={`Agregar ${ingrediente.nombre}`}
                            disabled={enMaximo}
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={() =>
                              cambiarCantidadIngrediente(ingrediente, 1)
                            }
                          >
                            +
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {!modoEdicionOpciones && ofreceCategoriaExtras && (
              <div className="mb-3 text-start">
                <button
                  type="button"
                  className="btn btn-outline-secondary mb-2"
                  aria-expanded={categoriaExtrasAbierta}
                  onClick={() => setCategoriaExtrasAbierta((prev) => !prev)}
                >
                  {categoriaExtrasAbierta ? "Ocultar extras" : "Extras"}
                </button>

                {categoriaExtrasAbierta &&
                  extrasDisponibles.map((extra) => (
                    <div key={extra.id} className="form-check mb-1">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        value={extra.id}
                        id={`extra-${extra.id}`}
                        checked={extrasSeleccionados.includes(extra.id)}
                        onChange={() => toggleExtra(extra.id)}
                      />
                      <label className="form-check-label" htmlFor={`extra-${extra.id}`}>
                        {extra.nombre}{" "}
                        <span className="text-muted">
                          +${formatearMonto(extra.precio)}
                        </span>
                      </label>
                    </div>
                  ))}
              </div>
            )}

            {!requiereSelector && (
              <div className="mb-3 text-start">
                <label
                  htmlFor="comentario"
                  className="fw-bold mb-2 modal-producto__comentario-label"
                >
                  Comentario (indicaciones para tu pedido)
                </label>
                <textarea
                  id="comentario"
                  className="form-control rounded-3 modal-producto__comentario"
                  placeholder="Ej: sin cebolla, bien fría, dejar en portería, etc."
                  value={comentario}
                  onChange={(e) => setComentario(e.target.value)}
                />
              </div>
            )}

            {!modoEdicionOpciones && (
              <div className="fw-bold mt-3 modal-producto__total">
                Total: ${formatearMonto(precioTotal)}
              </div>
            )}
          </div>

          <div className="modal-footer border-0 justify-content-center gap-3">
            <button
              className="btn btn-dark rounded-pill px-4"
              onClick={handleAgregar}
              disabled={!seleccionCompleta}
              style={{ fontWeight: 600, fontSize: 16 }}
            >
              {requiereSelector
                ? `${modoEdicionOpciones ? "Guardar" : "Agregar"} · $${formatearMonto(
                    precioTotal
                  )}`
                : modoEdicionOpciones
                  ? "Guardar"
                  : "Agregar al carrito"}
            </button>

            <button
              className="btn btn-outline-dark rounded-pill px-4"
              onClick={onCerrar}
            >
              Cancelar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
